import React, { useState, useEffect } from 'react';
import { formatCurrency, COUNTRY_CONFIGS } from '../utils/formatters';
import HomeIcon from './HomeIcon';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    filter: ['M22 3H2l8 9.46V19l4 2v-8.54L22 3z'],
    calendar: ['M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18'],
    fileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M14 9H8']
};

export default function FinancialReports({ user, operatorDetails }) {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currency, setCurrency] = useState('KWD');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });

    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);

    const fetchSummary = async () => {
        if (!operatorId) return;
        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            if (dateRange.start) queryParams.append('startDate', dateRange.start);
            if (dateRange.end) queryParams.append('endDate', dateRange.end);

            const [summaryRes, operatorRes] = await Promise.all([
                fetch(`/api/api/financial-summary/${operatorId}?${queryParams.toString()}`),
                fetch(`/api/operators/${operatorId}`)
            ]);

            const summaryData = await summaryRes.json();
            const operatorData = await operatorRes.json();

            setSummary(summaryData);
            if (operatorData?.currency) {
                setCurrency(operatorData.currency);
            }
        } catch (err) {
            console.error('Error fetching financial summary:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary();
    }, [operatorId]);

    const formatAmount = (amount) => {
        return formatCurrency(amount, operatorDetails?.country);
    };

    return (
        <div className="operator-page">
            <style>{`
                .report-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 24px;
                    margin-bottom: 32px;
                }
                .report-card {
                    background: white;
                    border-radius: 18px;
                    padding: 24px;
                    border: 1px solid #e2e8f0;
                    border-top: 4px solid #2563eb;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    transition: all 0.3s ease;
                }
                .report-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px -5px rgba(0,0,0,0.08);
                }
                .report-card-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: #eff6ff;
                    color: #2563eb;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 8px;
                }
                .report-card-title {
                    font-size: 11px;
                    font-weight: 800;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .report-card-value {
                    font-size: 28px;
                    font-weight: 800;
                    color: #1e293b;
                }
                .report-card-sub {
                    font-size: 12px;
                    color: #64748b;
                }
                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 80px 40px;
                    color: #94a3b8;
                    text-align: center;
                }
                .empty-icon {
                    width: 64px;
                    height: 64px;
                    background: #f1f5f9;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 20px;
                }
                .profit-badge {
                    padding: 4px 12px;
                    border-radius: 99px;
                    font-size: 12px;
                    font-weight: 700;
                    display: inline-flex;
                }
                .profit-up { background: #f0fdf4; color: #16a34a; }
                .profit-down { background: #fef2f2; color: #dc2626; }
                
                .expense-details {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    margin-top: 8px;
                    padding-top: 16px;
                    border-top: 1px dashed #e2e8f0;
                }
                .expense-item {
                    display: flex;
                    justify-content: space-between;
                    font-size: 13px;
                }
                .expense-label { color: #64748b; }
                .expense-val { color: #1e293b; font-weight: 600; }
            `}</style>

            <div className="op-breadcrumb">
                <HomeIcon />
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Financial Reports</span>
            </div>

            <div className="op-header">
                <div className="op-header-left">
                    <h1 className="op-title">Financial Intelligence</h1>
                    <p className="op-subtitle">Comprehensive analysis of revenue, expenses, and profit margins</p>
                </div>
                <div className="op-header-right">
                    <button className="op-create-btn" style={{ background: '#0f172a' }}>
                        <Icon d={icons.download} size={14} />
                        Export Data
                    </button>
                </div>
            </div>

            <div className="report-grid">
                <div className="report-card" style={{ borderTopColor: '#2563eb' }}>
                    <div className="report-card-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                        <Icon d={icons.fileText} size={22} />
                    </div>
                    <span className="report-card-title">Gross Revenue</span>
                    <span className="report-card-value">{loading ? '...' : formatAmount(summary?.totalRevenue)}</span>
                    <span className="report-card-sub">From {summary?.breakdown?.invoices || 0} invoices issued</span>
                    <div className="expense-details">
                        <div className="expense-item">
                            <span className="expense-label">Paid Amount</span>
                            <span className="expense-val" style={{ color: '#2563eb' }}>{formatAmount(summary?.paidRevenue)}</span>
                        </div>
                        <div className="expense-item">
                            <span className="expense-label">Pending Amount</span>
                            <span className="expense-val" style={{ color: '#0284c7' }}>{formatAmount((summary?.totalRevenue || 0) - (summary?.paidRevenue || 0))}</span>
                        </div>
                    </div>
                </div>

                <div className="report-card" style={{ borderTopColor: '#1d4ed8' }}>
                    <div className="report-card-icon" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
                        <Icon d={icons.fileText} size={22} />
                    </div>
                    <span className="report-card-title">Operating Expenses</span>
                    <span className="report-card-value">{loading ? '...' : formatAmount(summary?.totalExpenses)}</span>
                    <span className="report-card-sub">Combined fleet & operations cost</span>

                    <div className="expense-details">
                        <div className="expense-item">
                            <span className="expense-label">General & Admin</span>
                            <span className="expense-val">{formatAmount(summary?.totalGeneralExpenses)}</span>
                        </div>
                        <div className="expense-item">
                            <span className="expense-label">Fuel Consumption</span>
                            <span className="expense-val">{formatAmount(summary?.totalFuelExpenses)}</span>
                        </div>
                        <div className="expense-item">
                            <span className="expense-label">Fleet Maintenance</span>
                            <span className="expense-val">{formatAmount(summary?.totalMaintenanceExpenses)}</span>
                        </div>
                        <div className="expense-item">
                            <span className="expense-label">Spare Parts (Inventory)</span>
                            <span className="expense-val">{formatAmount(summary?.totalSparePartsExpenses)}</span>
                        </div>
                        <div className="expense-item">
                            <span className="expense-label">Driver Payroll (Est.)</span>
                            <span className="expense-val">{formatAmount(summary?.totalPayrollOverhead)}</span>
                        </div>
                    </div>
                </div>

                <div className="report-card" style={{ borderTopColor: '#0284c7' }}>
                    <div className="report-card-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                        <Icon d={icons.fileText} size={22} />
                    </div>
                    <span className="report-card-title">Net Profit (EBITDA)</span>
                    <span className="report-card-value" style={{ color: (summary?.netProfit >= 0) ? '#0f172a' : '#dc2626' }}>
                        {loading ? '...' : formatAmount(summary?.netProfit)}
                    </span>
                    <div style={{ marginTop: '8px' }}>
                        <span className={`profit-badge ${summary?.margin >= 0 ? 'profit-up' : 'profit-down'}`} style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}>
                            {loading ? '...' : `${summary?.margin?.toFixed(1)}% Margin`}
                        </span>
                    </div>
                    <span className="report-card-sub" style={{ marginTop: 'auto' }}>Calculated after all documented expenses</span>
                </div>
            </div>

            <div className="op-actions-bar">
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div className="op-search-box" style={{ width: '220px' }}>
                        <span className="search-icon"><Icon d={icons.calendar} size={14} /></span>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                        />
                    </div>
                    <div className="op-search-box" style={{ width: '220px' }}>
                        <span className="search-icon"><Icon d={icons.calendar} size={14} /></span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                        />
                    </div>
                    <button
                        className="op-create-btn"
                        onClick={fetchSummary}
                        style={{ background: '#0f172a', color: '#fff' }}
                    >
                        <Icon d={icons.filter} size={14} />
                        Generate Report
                    </button>
                    {(dateRange.start || dateRange.end) && (
                        <button
                            className="op-create-btn"
                            onClick={() => {
                                setDateRange({ start: '', end: '' });
                                // fetchSummary will be called via useEffect if I make it depend on dateRange, 
                                // but better to just call it here or use another useEffect.
                                // For now I'll just manual reset.
                            }}
                            style={{ background: 'transparent', color: '#64748b', border: 'none', padding: '0 8px' }}
                        >
                            Reset
                        </button>
                    )}
                </div>
            </div>

            <div className="op-table-container">
                <table className="op-table">
                    <thead>
                        <tr>
                            <th>DATA SOURCE</th>
                            <th>RECORDS</th>
                            <th>CONTRIBUTION</th>
                            <th>LAST UPDATED</th>
                            <th>TREND</th>
                            <th>STATUS</th>
                            <th>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary?.breakdown ? (
                            <>
                                <tr>
                                    <td>Client Invoices</td>
                                    <td>{summary.breakdown.invoices}</td>
                                    <td>{formatAmount(summary.totalRevenue)}</td>
                                    <td>Just now</td>
                                    <td><span style={{ color: '#16a34a' }}>↑ Revenue</span></td>
                                    <td><span className="op-status status-active">Live</span></td>
                                    <td><button className="op-action-btn">Detail</button></td>
                                </tr>
                                <tr>
                                    <td>General Expenses</td>
                                    <td>{summary.breakdown.expenses}</td>
                                    <td>{formatAmount(summary.totalGeneralExpenses)}</td>
                                    <td>Just now</td>
                                    <td><span style={{ color: '#dc2626' }}>↓ Expense</span></td>
                                    <td><span className="op-status status-active">Live</span></td>
                                    <td><button className="op-action-btn">Detail</button></td>
                                </tr>
                                <tr>
                                    <td>Fuel Consumption</td>
                                    <td>{summary.breakdown.fuelRecords}</td>
                                    <td>{formatAmount(summary.totalFuelExpenses)}</td>
                                    <td>Just now</td>
                                    <td><span style={{ color: '#64748b' }}>Steady</span></td>
                                    <td><span className="op-status status-active">Live</span></td>
                                    <td><button className="op-action-btn">Detail</button></td>
                                </tr>
                                <tr>
                                    <td>Fleet Maintenance</td>
                                    <td>{summary.breakdown.maintenanceJobs}</td>
                                    <td>{formatAmount(summary.totalMaintenanceExpenses)}</td>
                                    <td>Just now</td>
                                    <td><span style={{ color: '#2563eb' }}>Scheduled</span></td>
                                    <td><span className="op-status status-active">Live</span></td>
                                    <td><button className="op-action-btn">Detail</button></td>
                                </tr>
                                <tr>
                                    <td>Spare Parts (Tyre/Bat)</td>
                                    <td>{summary.breakdown.spareParts}</td>
                                    <td>{formatAmount(summary.totalSparePartsExpenses)}</td>
                                    <td>Just now</td>
                                    <td><span style={{ color: '#f59e0b' }}>Variable</span></td>
                                    <td><span className="op-status status-active">Live</span></td>
                                    <td><button className="op-action-btn">Detail</button></td>
                                </tr>
                                <tr>
                                    <td>Driver Salaries</td>
                                    <td>{summary.breakdown.drivers}</td>
                                    <td>{formatAmount(summary.totalPayrollOverhead)}</td>
                                    <td>Monthly (Est.)</td>
                                    <td><span style={{ color: '#64748b' }}>Fixed</span></td>
                                    <td><span className="op-status status-active">Live</span></td>
                                    <td><button className="op-action-btn">Detail</button></td>
                                </tr>
                            </>
                        ) : (
                            <tr>
                                <td colSpan="7">
                                    <div className="empty-state">
                                        <div className="empty-icon">
                                            <Icon d={icons.fileText} size={32} />
                                        </div>
                                        <h3 style={{ color: '#1e293b', marginBottom: '8px' }}>No Data Available</h3>
                                        <p style={{ maxWidth: '300px' }}>Financial summary could not be calculated. Please check your operator settings.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

